'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  getActivityListService,
  getRunningResourceActivitiesService,
} from '@/services/httpServices/ActivityServices';
import type {
  GetActivityListServiceConfig,
  GetRunningResourceActivitiesServiceConfig,
  UserActivityListResponse,
} from '@/services/httpServices/ActivityServices/types';

import { activityListQueryKey, infiniteActivityListKeys, runningResourceActivitiesQueryKey } from './activityQueryKeys';
import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { useQueryConfig } from './useQueryConfig';

/**
 * Hook for fetching paginated list of user activities
 * Used for populating the activity center/notifications panel
 */
export function useActivityListQuery(config: GetActivityListServiceConfig) {
  const { isEnabled } = useQueryConfig();
  const hasUserId = config.params?.params.id !== undefined
    && config.params?.params.id !== null
    && config.params?.params.id !== '';

  return useQuery({
    queryKey: activityListQueryKey(config),
    queryFn: async () => getActivityListService(config),
    enabled: isEnabled && hasUserId,
  });
}

/**
 * Hook to fetch activity list with infinite scrolling using cursor-based pagination
 * @param initialParams - Initial activity list query parameters
 * @param enabled - Whether to enable the query
 * @returns Infinite query result for the activity list
 */
export function useInfiniteActivityListQuery(
  initialParams: GetActivityListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();
  const hasUserId = initialParams.params?.params.id !== undefined
    && initialParams.params?.params.id !== null
    && initialParams.params?.params.id !== '';

  return useInfiniteQuery<UserActivityListResponse>({
    queryKey: infiniteActivityListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getActivityListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled && hasUserId,
  });
}

/**
 * Hook for fetching running activities for a specific resource
 * Used to show activity status on course/quiz/sequence pages
 */
export function useRunningResourceActivitiesQuery(
  config: GetRunningResourceActivitiesServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();
  const hasRequiredParams = config.params?.params.id !== undefined
    && config.params?.params.id !== ''
    && config.params?.params.spaceId !== undefined
    && config.params?.params.spaceId !== ''
    && config.params?.query.resourceId !== undefined
    && config.params?.query.resourceId !== '';

  return useQuery({
    queryKey: runningResourceActivitiesQueryKey(config),
    queryFn: async () => getRunningResourceActivitiesService(config),
    enabled: isEnabled && enabled && hasRequiredParams,
  });
}
